import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import {
  HomeRegular,
  DocumentBulletListRegular,
  ContactCardRegular,
  ArrowSwapRegular,
  PeopleTeamRegular,
  NoteRegular,
  BookQuestionMarkRegular,
  WeatherMoon20Regular,
  WeatherSunny20Regular,
} from '@fluentui/react-icons';
import { useThemeMode } from '../../theme/themeMode';
import MomentHost from '../moments/MomentHost';
import MomentsToggle from '../moments/MomentsToggle';
import SyncFreshness from './SyncFreshness';
import { FeedbackMenu } from '../feedback/FeedbackMenu';
import LanguageToggle from './LanguageToggle';

// Sidebar nav mirrors the ptc-tax-lifecycle family layout: blue header + left rail +
// gray body. Icons from @fluentui/react-icons so the look matches without adding deps.

// The theme toggle sits on the header bar. In light the bar is the brand-blue
// gradient, so the glyph is white with a translucent-white hover (Fluent's
// default subtle hover — a light gray — would vanish on blue). In dark the bar
// is the deep #151a21 surface, so the theme's own neutral glyph/hover apply.
const useStyles = makeStyles({
  themeToggleLight: {
    color: '#ffffff',
    ':hover': { color: '#ffffff', backgroundColor: 'rgba(255, 255, 255, 0.16)' },
    ':hover:active': { color: '#ffffff', backgroundColor: 'rgba(255, 255, 255, 0.24)' },
  },
  themeToggleDark: {
    color: tokens.colorNeutralForeground2,
    ':hover': { color: tokens.colorNeutralForeground1 },
  },
});

interface Props {
  children: ReactNode;
}

export default function AppLayout({ children }: Props) {
  const { t } = useTranslation();
  const { mode, toggleMode } = useThemeMode();
  const styles = useStyles();

  const navItems = [
    { to: '/', icon: <HomeRegular />, label: t('nav.dashboard'), end: true },
    { to: '/compliance/tasks', icon: <DocumentBulletListRegular />, label: t('nav.tasks'), end: false },
    { to: '/clients', icon: <ContactCardRegular />, label: t('nav.clients'), end: false },
    { to: '/conversion', icon: <ArrowSwapRegular />, label: t('nav.conversion'), end: false },
    { to: '/management', icon: <PeopleTeamRegular />, label: t('nav.management'), end: false },
    { to: '/patch-notes', icon: <NoteRegular />, label: t('nav.patchNotes'), end: false },
    { to: '/quick-start', icon: <BookQuestionMarkRegular />, label: t('nav.quickStart'), end: false },
  ];

  const themeToggleLabel =
    mode === 'dark' ? t('common.theme.toLight') : t('common.theme.toDark');

  return (
    // MomentHost is mounted ONCE, here at the root of the signed-in app (accomplishment moments).
    <MomentHost>
    <div className="app-container">
      <header className="app-header">
        <div className="app-header-brand">
          {/* i18n-ignore: the brand mark, the same in every language */}
          <span className="app-header-mark" aria-hidden="true">PTC</span>
          <h1>{t('brand')}</h1>
        </div>
        <div className="app-header-actions">
          <SyncFreshness />
          <MomentsToggle className={mode === 'dark' ? styles.themeToggleDark : styles.themeToggleLight} />
          <Tooltip content={themeToggleLabel} relationship="label">
            <Button
              appearance="subtle"
              shape="circular"
              className={mode === 'dark' ? styles.themeToggleDark : styles.themeToggleLight}
              icon={mode === 'dark' ? <WeatherSunny20Regular /> : <WeatherMoon20Regular />}
              onClick={toggleMode}
            />
          </Tooltip>
          {/* Fleet feedback contract v1: Report a problem / Suggest an improvement, on every staff page. */}
          <FeedbackMenu className={mode === 'dark' ? styles.themeToggleDark : styles.themeToggleLight} />
          <LanguageToggle />
        </div>
      </header>
      <div className="app-body">
        <nav className="app-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => (isActive ? 'active' : '')}
            >
              {item.icon}
              {item.label}
            </NavLink>
          ))}
        </nav>
        <main className="app-content">{children}</main>
      </div>
    </div>
    </MomentHost>
  );
}
